// Pins on the page side (port of web/pins.js): colours from the tokens, the pin look as SVG for the DOM, emoji glyphs
// for the DOM, group ranks, fading, and one map feature per event and look (cached, so redrawing doesn't rebuild
// thousands of features). The map's pin and group images are painted by paint.ts, in a worker (images.ts).
import type * as GeoJSON from "geojson";
import { SHARED } from "../shared/config";
import { RULES, type Level, type Look, type Tone } from "../shared/rules";
import { fillEmoji, GROUP_MAX, OUTLINE, pathOf, setColors, shapePoints } from "./paint";

export { GROUP_MAX, shapePoints };
import type { FeedEvent } from "../data/event";

export const PIN_SIZE = SHARED.pins.size;
export const TONES: readonly Tone[] = ["plain", "soon", "now"];

// ---------- colours, from the tokens (src/shared/tokens.css) ----------
// Read once per colour (and again when the system switches light / dark): reading styles is slow.
const colorCache = new Map<string, string>();
if (typeof matchMedia === "function") matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => colorCache.clear());
/** A token's colour as rgb(), for canvas and SVG ("accent", "now-fill", or a camelCase name). */
export function cssColor(name: string) {
  const css = name.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
  let c = colorCache.get(css);
  if (!c) {
    c = `rgb(${getComputedStyle(document.documentElement).getPropertyValue(`--${css}-rgb`).trim()})`;
    colorCache.set(css, c);
  }
  return c;
}
// Painting on the page (badges' emoji, and pins where there's no image worker) reads the same tokens.
if (typeof document !== "undefined") setColors(cssColor);

// ---------- emoji codes: a number per emoji so groups can carry their best member's in a map expression ----------
const emojiIndex = new Map<string, number>(), emojiList: string[] = [];
export function emojiCode(emoji: string) {
  let i = emojiIndex.get(emoji);
  if (i == null) { i = emojiList.length; emojiIndex.set(emoji, i); emojiList.push(emoji); }
  return i;
}
export const emojiOfCode = (code: number) => emojiList[code] ?? SHARED.fallbackEmoji;

/** The same as SVG markup, sized in px (half-size r): the body and its border. */
const svgCache = new Map<string, string>();
export function shapeSVG(style: Pick<Look, "outline" | "shape">, r: number) {
  const key = `${style.outline}|${style.shape}|${r}`;
  let s = svgCache.get(key);
  if (!s) {
    const o = OUTLINE[style.outline];
    s = `<path d="${pathOf(shapePoints(style.shape, r))}" fill="${cssColor(o.fill)}"/>` +
      `<path d="${pathOf(shapePoints(style.shape, r - o.width / 2))}" fill="none" stroke="${cssColor(o.color)}" stroke-width="${o.width}" stroke-linejoin="round"/>`;
    svgCache.set(key, s);
  }
  return s;
}

/** Draws an emoji centred into a canvas of `size` css px (for DOM glyphs: badges, pointers). */
export function paintEmojiGlyph(c: HTMLCanvasElement, emoji: string, px: number) {
  const ratio = Math.max(2, Math.ceil(devicePixelRatio || 1)), size = Math.ceil(px * 1.4);
  c.width = c.height = size * ratio;
  c.style.width = c.style.height = `${size}px`;
  const ctx = c.getContext("2d")!;
  ctx.scale(ratio, ratio);
  ctx.fillStyle = cssColor("text"); // opaque, so the emoji isn't drawn see-through
  fillEmoji(ctx, emoji, size / 2, size / 2, px);
}

// ---------- groups ----------
export interface GroupMember { emoji: string; level: Level; start: Date }
export const groupFace = (members: readonly GroupMember[]) => RULES.groupPin(members);
// A member's rank as one number, smaller = better (interest level, then start, then emoji): the cluster keeps its
// smallest ("min" reduce), so a group shows its best member's emoji before its other members are looked up.
const LEVEL_RANK: Record<Level, number> = { interest: 0, none: 1 }, LEVEL_NAME: Level[] = ["interest", "none"];
const RANK_START = 1e4, RANK_LEVEL = 1e12;
const memberRank = (level: Level, start: Date, code: number) =>
  LEVEL_RANK[level] * RANK_LEVEL + Math.floor(+start / 60e3) * RANK_START + code;
export const rankMember = (rank: number): GroupMember => ({
  level: LEVEL_NAME[Math.floor(rank / RANK_LEVEL)] ?? "none",
  start: new Date(Math.floor((rank % RANK_LEVEL) / RANK_START) * 60e3),
  emoji: emojiOfCode(rank % RANK_START),
});

/** What a pin image id ("pin|<level>|<emoji code>|<free 0/1>|<tone rank>", one per look) draws. */
export function pinImageArgs(id: string): { emoji: string; diameter: number; style: Look } {
  const [, level, code, free, tone] = id.split("|");
  return { emoji: emojiOfCode(Number(code)), diameter: PIN_SIZE[level as Level] ?? PIN_SIZE.none,
    style: RULES.pinFace(TONES[Number(tone)] ?? "plain", free === "1") };
}

// ---------- fading (unknown end) and when a pin's look next changes ----------
// A pin whose end is unknown fades (RULES.pinOpacity) in FADE_STEP steps, so its feature changes a few times an hour.
const FADE_STEP = 10 * 60e3;
const fadeFrom = (e: FeedEvent) => +e.start + SHARED.unknownEnd.fadeAfterHours * 3600e3;
export function pinFade(e: FeedEvent, now: Date) {
  if (!RULES.endUnknown(e) || +now <= fadeFrom(e)) return 1;
  const from = fadeFrom(e);
  return Math.round(RULES.pinOpacity(e, new Date(from + Math.floor((+now - from) / FADE_STEP) * FADE_STEP)) * 1000) / 1000;
}
/** When an event's pin next changes look with time alone (turns soon, starts, fades a step, ends). */
export function nextLookChange(e: FeedEvent, now: Date) {
  const times = [+e.start - SHARED.pins.soonMinutes * 60e3, +e.start, +RULES.effectiveEnd(e)];
  if (RULES.endUnknown(e)) times.push(fadeFrom(e) + (Math.max(0, Math.floor((+now - fadeFrom(e)) / FADE_STEP)) + 1) * FADE_STEP);
  return Math.min(...times.filter((t) => t > +now), Infinity);
}

// ---------- one map feature per event and look ----------
export interface PinProps { id: string; icon: string; tone: number; free: number; opacity: number; rank: number; sort: number }
export type PinFeature = GeoJSON.Feature<GeoJSON.Point, PinProps>;
const features = new WeakMap<FeedEvent, { key: string; feature: PinFeature }>();
export function pinFeature(e: FeedEvent, now: Date, level: Level, emoji: string): PinFeature {
  const code = emojiCode(emoji), free = RULES.isFreeForPin(e) ? 1 : 0;
  const tone = TONES.indexOf(RULES.timeTone(e, now)), happening = e.start <= now ? 1 : 0, opacity = pinFade(e, now);
  const icon = `pin|${level}|${code}|${free}|${tone}`, key = `${icon}|${happening}|${opacity}`;
  const cached = features.get(e);
  if (cached?.key === key) return cached.feature;
  const feature: PinFeature = {
    type: "Feature", geometry: { type: "Point", coordinates: [e.lng, e.lat] },
    properties: {
      id: e.id, icon, tone, free, opacity, rank: memberRank(level, e.start, code),
      // Draw order: bigger (better-matching) pins on top; happening now above later (ios MapPin.zPriority).
      sort: (level === "interest" ? 500 : 200) + (happening ? 150 : 0),
    },
  };
  features.set(e, { key, feature });
  return feature;
}
